import { useState, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Upload, Zap, Flame, FileText, X, AlertTriangle, Info,
  TrendingDown, CheckCircle, Eye, EyeOff, Loader2,
  ArrowRight, ShieldAlert, HelpCircle, ChevronDown,
  BarChart3, Lightbulb, FileWarning, Save, Download,
  RefreshCw, ArrowDown, Bookmark
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── Glossary Tooltip ─── */
const GlossaryTip = ({ term, glossario }) => {
  const [open, setOpen] = useState(false);
  const entry = glossario?.[term];
  if (!entry) return null;
  return (
    <span className="relative inline-block ml-1">
      <button onClick={() => setOpen(!open)} className="text-gray-400 hover:text-green-700 transition" data-testid={`glossary-${term}`}>
        <HelpCircle className="w-3.5 h-3.5" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="absolute z-50 left-0 top-6 w-72 bg-white border border-gray-200 rounded-xl shadow-xl p-3.5 text-left">
            <p className="text-xs font-bold text-green-800 mb-1">{entry.titolo}</p>
            <p className="text-[11px] text-gray-600 leading-relaxed">{entry.descrizione}</p>
            {entry.dettaglio && <p className="text-[10px] text-gray-400 mt-1.5 font-mono">{entry.dettaglio}</p>}
          </motion.div>
        )}
      </AnimatePresence>
    </span>
  );
};

/* ─── Cost comparison row ─── */
const CostRow = ({ label, origVal, eightVal, glossKey, glossario, alt }) => {
  const orig = Number(origVal || 0);
  const eight = Number(eightVal || 0);
  const diff = orig - eight;
  const hasDiff = Math.abs(diff) > 0.5;
  return (
    <div className={`grid grid-cols-12 gap-2 items-center py-2.5 px-4 rounded-lg transition ${alt ? "bg-gray-50" : "hover:bg-gray-50"}`}>
      <div className="col-span-5 flex items-center gap-1">
        <span className="text-[13px] text-gray-700">{label}</span>
        {glossKey && <GlossaryTip term={glossKey} glossario={glossario} />}
      </div>
      <div className="col-span-2 text-right"><span className="text-[13px] font-mono text-gray-900">€{orig.toFixed(2)}</span></div>
      <div className="col-span-1 flex justify-center"><ArrowRight className="w-3 h-3 text-gray-300" /></div>
      <div className="col-span-2 text-right"><span className="text-[13px] font-mono text-green-700 font-medium">€{eight.toFixed(2)}</span></div>
      <div className="col-span-2 text-right">
        {hasDiff && <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${diff > 0 ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>{diff > 0 ? `-€${diff.toFixed(2)}` : `+€${Math.abs(diff).toFixed(2)}`}</span>}
      </div>
    </div>
  );
};

/* ─── Hidden Cost Card ─── */
const HiddenCostCard = ({ cost }) => {
  const types = { penale: "Penale", servizio_accessorio: "Servizio", spread_eccessivo: "Spread", quota_anomala: "Anomalia", addebito_extra: "Extra" };
  return (
    <div className="border border-red-100 bg-red-50/50 rounded-xl p-3" data-testid="hidden-cost-item">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-bold uppercase tracking-wider text-red-500">{types[cost.tipo] || cost.tipo}</span>
        <span className="text-sm font-bold text-red-600">€{Number(cost.importo || 0).toFixed(2)}</span>
      </div>
      <p className="text-xs font-medium text-gray-800">{cost.voce}</p>
      <p className="text-[11px] text-gray-500 mt-0.5">{cost.spiegazione}</p>
    </div>
  );
};


/* ═══════════════════════════════════════════════ */
/* ─── MAIN COMPONENT ─── */
/* ═══════════════════════════════════════════════ */
const SupplyTypeToggle = ({ tipo, onChange, disabled, classificazione }) => {
  const source = classificazione?.manuale ? "Impostato manualmente" : classificazione?.corretto ? `Corretto automaticamente (AI diceva ${classificazione.tipo_ai})` : classificazione?.segnali?.length ? `Rilevato da: ${classificazione.segnali.join(", ")}` : "Rilevato dall'AI";
  return (
    <div className="flex items-center gap-2" data-testid="supply-type-toggle">
      <div className="inline-flex bg-gray-100 rounded-lg p-0.5">
        {[["luce", Zap, "Luce", "text-amber-600"], ["gas", Flame, "Gas", "text-orange-600"]].map(([t, Icon, label, color]) => (
          <button key={t} type="button" disabled={disabled} onClick={() => onChange(t)} data-testid={`supply-type-${t}-btn`}
            className={`flex items-center gap-1 px-3 h-8 rounded-md text-xs font-semibold transition-colors ${tipo === t ? `bg-white shadow-sm ${color}` : "text-gray-500 hover:text-gray-800"}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>
      <span className={`text-[10px] max-w-[220px] truncate ${classificazione?.corretto ? "text-amber-600" : "text-gray-400"}`} title={source} data-testid="supply-type-source">{source}</span>
    </div>
  );
};

export default function BillSimulator() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [data, setData] = useState(null);
  const [showPdf, setShowPdf] = useState(true);
  const [showGlossary, setShowGlossary] = useState(false);
  const [offers, setOffers] = useState([]);
  const [selectedOfferId, setSelectedOfferId] = useState("");
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetch(`${API}/admin/offers`, { credentials: "include" })
      .then(r => r.json()).then(setOffers).catch(() => {});
  }, []);

  const filteredOffers = useMemo(() => {
    if (!data) return offers;
    const t = data.identificativi?.tipo;
    return offers.filter(o => o.tipo === t || o.tipo === "dual");
  }, [offers, data]);

  const pdfUrl = useMemo(() => {
    if (!data?.file_b64) return null;
    const bytes = atob(data.file_b64);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    return URL.createObjectURL(new Blob([arr], { type: "application/pdf" }));
  }, [data?.file_b64]);

  const handleSimulate = async () => {
    if (!file) return;
    setLoading(true); setData(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (selectedOfferId) fd.append("offer_id", selectedOfferId);
      const res = await fetch(`${API}/admin/simulate-bill`, { method: "POST", body: fd, credentials: "include" });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || "Errore"); }
      const result = await res.json();
      setData(result);
      if (result.confronto?.offer_id) setSelectedOfferId(result.confronto.offer_id);
      toast.success("Simulazione completata");
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  const handleRecalculate = async (offerId) => {
    if (!data?.extracted_data || !offerId) return;
    setRecalculating(true); setSelectedOfferId(offerId);
    try {
      const res = await fetch(`${API}/admin/recalculate-simulation`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ extracted_data: data.extracted_data, offer_id: offerId })
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || "Errore"); }
      const result = await res.json();
      setData(prev => ({ ...prev, ...result }));
      toast.success("Ricalcolo completato");
    } catch (e) { toast.error(e.message); }
    finally { setRecalculating(false); }
  };

  const handleChangeTipo = async (tipo) => {
    if (!data?.extracted_data || data.identificativi?.tipo === tipo) return;
    setRecalculating(true);
    try {
      const res = await fetch(`${API}/admin/recalculate-simulation`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ extracted_data: data.extracted_data, offer_id: "", tipo })
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || "Errore"); }
      const result = await res.json();
      setData(prev => ({ ...prev, ...result, classificazione: { ...(prev.classificazione || {}), tipo, manuale: true } }));
      setSelectedOfferId(result.confronto?.offer_id || "");
      toast.success(`Fornitura impostata su ${tipo === "luce" ? "Luce" : "Gas"}`);
    } catch (e) { toast.error(e.message); }
    finally { setRecalculating(false); }
  };

  const handleSave = async () => {
    if (!data) return; setSaving(true);
    try {
      const res = await fetch(`${API}/admin/save-simulation`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({
          offer_id: data.confronto?.offer_id || selectedOfferId, offer_name: data.confronto?.offerta_8energy,
          identificativi: data.identificativi, consumi: data.consumi,
          unbundling_originale: data.unbundling_originale, unbundling_8energy: data.unbundling_8energy,
          confronto: data.confronto, costi_nascosti: data.costi_nascosti
        })
      });
      if (!res.ok) throw new Error("Errore salvataggio");
      const result = await res.json();
      toast.success(`Simulazione salvata: ${result.simulation_id}`);
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleExport = async () => {
    if (!data) return; setExporting(true);
    try {
      const res = await fetch(`${API}/admin/export-simulation-pdf`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({
          identificativi: data.identificativi, consumi: data.consumi,
          unbundling_originale: data.unbundling_originale, unbundling_8energy: data.unbundling_8energy,
          confronto: data.confronto, costi_nascosti: data.costi_nascosti
        })
      });
      const result = await res.json();
      if (result.pdf_b64) {
        const bytes = atob(result.pdf_b64);
        const arr = new Uint8Array(bytes.length);
        for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
        const url = URL.createObjectURL(new Blob([arr], { type: "application/pdf" }));
        const a = document.createElement("a"); a.href = url; a.download = result.filename; a.click();
        toast.success("PDF esportato");
      } else if (result.html) {
        const blob = new Blob([result.html], { type: "text/html" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = result.filename; a.click();
        toast.success("HTML esportato");
      }
    } catch (e) { toast.error(e.message); }
    finally { setExporting(false); }
  };

  const id = data?.identificativi || {};
  const cons = data?.consumi || {};
  const orig = data?.unbundling_originale || {};
  const proj = data?.unbundling_8energy || {};
  const conf = data?.confronto || {};
  const hidden = data?.costi_nascosti || [];
  const glossario = data?.glossario || {};
  const isLuce = id.tipo === "luce";

  return (
    <div className="bg-white min-h-screen" data-testid="bill-simulator">
      {/* ── STEP 1: SETUP ── */}
      {!data && !loading && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl mx-auto pt-12 px-4">
          <div className="text-center mb-10">
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-green-50 to-green-100 rounded-2xl flex items-center justify-center">
              <BarChart3 className="w-8 h-8 text-green-700" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Simulatore Bolletta</h1>
            <p className="text-gray-500 mt-2 text-base">Carica una bolletta e confronta con le tariffe 8energy</p>
          </div>

          {/* Offer Selection */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Offerta Target</label>
            <Select value={selectedOfferId} onValueChange={setSelectedOfferId}>
              <SelectTrigger className="w-full h-12 bg-gray-50 border-gray-200 text-gray-900 rounded-xl" data-testid="offer-select">
                <SelectValue placeholder="Seleziona un'offerta (opzionale)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Migliore automatica</SelectItem>
                {offers.map(o => (
                  <SelectItem key={o.offer_id} value={o.offer_id}>
                    <span className="flex items-center gap-2">
                      {o.tipo === "luce" ? <Zap className="w-3 h-3 text-amber-500" /> : o.tipo === "dual" ? <span className="flex"><Zap className="w-3 h-3 text-amber-500" /><Flame className="w-3 h-3 text-orange-500" /></span> : <Flame className="w-3 h-3 text-orange-500" />}
                      {o.nome} — {o.tipo === "gas" ? `€${o.prezzo_smc}/Smc` : `€${o.prezzo_kwh}/kWh`}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Upload Area */}
          <div
            className={`border-2 border-dashed rounded-2xl p-10 text-center transition-all cursor-pointer ${file ? "border-green-400 bg-green-50/50" : "border-gray-200 hover:border-green-300 hover:bg-green-50/30"}`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f?.type === "application/pdf") setFile(f); }}
            onClick={() => document.getElementById("sim-file-input").click()}
          >
            <input type="file" accept=".pdf" id="sim-file-input" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileText className="w-10 h-10 text-green-600" />
                <div className="text-left">
                  <p className="text-sm font-semibold text-green-800">{file.name}</p>
                  <p className="text-xs text-green-600">{(file.size / 1024).toFixed(0)} KB</p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <Upload className="w-12 h-12 mx-auto text-gray-300" />
                <p className="text-base text-gray-500">Trascina qui la bolletta PDF</p>
                <p className="text-xs text-gray-400">oppure clicca per selezionare</p>
              </div>
            )}
          </div>

          <div className="flex gap-3 mt-6">
            <Button onClick={handleSimulate} disabled={!file} className="flex-1 h-12 rounded-xl bg-green-700 hover:bg-green-800 text-white text-base font-semibold" data-testid="simulate-btn">
              <Zap className="w-4 h-4 mr-2" /> Avvia Simulazione
            </Button>
            {file && (
              <Button variant="outline" onClick={() => { setFile(null); }} className="h-12 rounded-xl border-gray-200">
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </motion.div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex flex-col items-center justify-center pt-24 gap-5">
          <div className="relative w-20 h-20">
            <div className="absolute inset-0 border-4 border-green-100 rounded-full" />
            <div className="absolute inset-0 border-4 border-transparent border-t-green-600 rounded-full animate-spin" />
            <Zap className="w-7 h-7 text-green-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>
          <div className="text-center">
            <p className="text-lg font-semibold text-gray-800">Analisi in corso</p>
            <p className="text-sm text-gray-400 mt-1">Estrazione dati e calcolo unbundling...</p>
          </div>
        </div>
      )}

      {/* ── STEP 2: RESULTS ── */}
      {data && !loading && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          {/* Top Toolbar */}
          <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-gray-100 px-6 py-3">
            <div className="flex items-center justify-between max-w-[1600px] mx-auto">
              <div className="flex items-center gap-4">
                <SupplyTypeToggle tipo={id.tipo} onChange={handleChangeTipo} disabled={recalculating} classificazione={data.classificazione} />
                {/* Offer Switcher */}
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider hidden sm:inline">Offerta:</span>
                  <Select value={selectedOfferId || "auto"} onValueChange={(v) => handleRecalculate(v === "auto" ? "" : v)}>
                    <SelectTrigger className="w-56 h-9 bg-gray-50 border-gray-200 rounded-lg text-sm" data-testid="offer-switcher">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredOffers.map(o => (
                        <SelectItem key={o.offer_id} value={o.offer_id}>
                          <span className="flex items-center gap-2">{o.tipo === "luce" ? <Zap className="w-3 h-3 text-amber-500" /> : <Flame className="w-3 h-3 text-orange-500" />} {o.nome}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {recalculating && <Loader2 className="w-4 h-4 animate-spin text-green-600" />}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowPdf(!showPdf)} className="text-xs rounded-lg border-gray-200" data-testid="toggle-pdf">
                  {showPdf ? <EyeOff className="w-3.5 h-3.5 mr-1.5" /> : <Eye className="w-3.5 h-3.5 mr-1.5" />}
                  PDF
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowGlossary(!showGlossary)} className="text-xs rounded-lg border-gray-200" data-testid="toggle-glossary">
                  <Lightbulb className="w-3.5 h-3.5 mr-1.5" /> Glossario
                </Button>
                <Button variant="outline" size="sm" onClick={handleSave} disabled={saving} className="text-xs rounded-lg border-gray-200" data-testid="save-btn">
                  {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
                  Salva
                </Button>
                <Button size="sm" onClick={handleExport} disabled={exporting} className="text-xs rounded-lg bg-green-700 hover:bg-green-800 text-white" data-testid="export-btn">
                  {exporting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
                  Esporta PDF
                </Button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className={`grid ${showPdf ? "grid-cols-1 xl:grid-cols-2" : "grid-cols-1 max-w-4xl mx-auto"} gap-6 p-6`}>
            {/* LEFT: Simulation Results */}
            <div className="space-y-5 order-2 xl:order-1">
              {/* Customer Header */}
              <div className="flex items-center justify-between border-b border-gray-100 pb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isLuce ? "bg-amber-50" : "bg-orange-50"}`}>
                    {isLuce ? <Zap className="w-5 h-5 text-amber-600" /> : <Flame className="w-5 h-5 text-orange-600" />}
                  </div>
                  <div>
                    <p className="text-base font-semibold text-gray-900">{id.intestatario || "Cliente"}</p>
                    <p className="text-xs text-gray-400 font-mono">{id.pod_pdr} | {id.periodo}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-400">Fornitore attuale</p>
                  <p className="text-sm font-medium text-gray-600">{id.fornitore || "N/A"}</p>
                </div>
              </div>

              {/* Savings Card */}
              <div className={`rounded-2xl p-6 ${conf.risparmio_periodo > 0 ? "bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200" : "bg-gradient-to-br from-red-50 to-orange-50 border border-red-200"}`} data-testid="savings-banner">
                <div className="grid grid-cols-4 gap-4 items-center text-center">
                  <div>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Attuale</p>
                    <p className="text-2xl font-bold text-gray-800">€{conf.totale_originale?.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">8energy</p>
                    <p className="text-2xl font-bold text-green-700">€{conf.totale_8energy?.toFixed(2)}</p>
                    <p className="text-[10px] text-gray-400">{conf.offerta_8energy}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Risparmio</p>
                    <p className={`text-2xl font-bold ${conf.risparmio_periodo > 0 ? "text-green-700" : "text-red-600"}`}>
                      {conf.risparmio_periodo > 0 ? "-" : "+"}€{Math.abs(conf.risparmio_periodo || 0).toFixed(2)}
                    </p>
                    <p className="text-[10px] text-gray-400">{conf.percentuale_risparmio}%</p>
                  </div>
                  <div className="border-l border-gray-200 pl-4">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Annuale</p>
                    <p className={`text-3xl font-extrabold ${conf.risparmio_annuale > 0 ? "text-green-700" : "text-red-600"}`}>€{Math.abs(conf.risparmio_annuale || 0).toFixed(0)}</p>
                  </div>
                </div>
              </div>

              {/* Unbundling Table */}
              <div className="rounded-2xl border border-gray-100 bg-white" data-testid="unbundling-table">
                <div className="px-4 py-3 border-b border-gray-100">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400">Unbundling dei Costi</h3>
                </div>
                <div className="grid grid-cols-12 gap-2 items-center py-2 px-4 bg-gray-50">
                  <div className="col-span-5 text-[10px] font-bold text-gray-400 uppercase">Voce</div>
                  <div className="col-span-2 text-right text-[10px] font-bold text-gray-400 uppercase">Attuale</div>
                  <div className="col-span-1" />
                  <div className="col-span-2 text-right text-[10px] font-bold text-green-600 uppercase">8energy</div>
                  <div className="col-span-2 text-right text-[10px] font-bold text-gray-400 uppercase">Delta</div>
                </div>
                <CostRow label="Spesa Materia Energia" origVal={orig.spesa_materia_energia} eightVal={proj.spesa_materia_energia} glossKey="spesa_materia_energia" glossario={glossario} alt />
                <CostRow label="Trasporto e Gestione" origVal={orig.spesa_trasporto_gestione} eightVal={proj.spesa_trasporto_gestione} glossKey="spesa_trasporto_gestione" glossario={glossario} />
                <CostRow label="Oneri di Sistema" origVal={orig.oneri_di_sistema} eightVal={proj.oneri_di_sistema} glossKey="oneri_di_sistema" glossario={glossario} alt />
                <CostRow label="Imposte e IVA" origVal={orig.imposte_iva} eightVal={proj.imposte_iva} glossKey="imposte_iva" glossario={glossario} />
                {Number(orig.altre_voci || 0) > 0 && <CostRow label="Altre Voci" origVal={orig.altre_voci} eightVal={0} glossario={glossario} alt />}
                <CostRow label="Quota Fissa / PCV" origVal={orig.quota_fissa_mensile ? Number(orig.quota_fissa_mensile) * (id.mesi_periodo || 1) : 0} eightVal={proj.quota_fissa} glossKey="quota_fissa" glossario={glossario} />
                {Number(proj.sconti_applicati || 0) > 0 && (
                  <div className="grid grid-cols-12 gap-2 items-center py-2.5 px-4 bg-green-50">
                    <div className="col-span-5 text-[13px] text-green-700 font-medium">Sconti Applicati</div>
                    <div className="col-span-2" /><div className="col-span-1" />
                    <div className="col-span-2 text-right"><span className="text-[13px] font-mono text-green-700">-€{Number(proj.sconti_applicati).toFixed(2)}</span></div>
                    <div className="col-span-2" />
                  </div>
                )}
                {/* Total */}
                <div className="grid grid-cols-12 gap-2 items-center py-3 px-4 bg-gray-50 border-t border-gray-200 rounded-b-2xl">
                  <div className="col-span-5 text-sm font-bold text-gray-900">TOTALE</div>
                  <div className="col-span-2 text-right"><span className="text-base font-bold text-gray-900">€{Number(orig.totale_bolletta || 0).toFixed(2)}</span></div>
                  <div className="col-span-1 flex justify-center"><ArrowRight className="w-3.5 h-3.5 text-gray-400" /></div>
                  <div className="col-span-2 text-right"><span className="text-base font-bold text-green-700">€{Number(proj.totale_bolletta || 0).toFixed(2)}</span></div>
                  <div className="col-span-2 text-right">
                    <span className={`text-xs font-bold px-2 py-1 rounded-full ${conf.risparmio_periodo > 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
                      {conf.risparmio_periodo > 0 ? "-" : "+"}€{Math.abs(conf.risparmio_periodo || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Price per Unit */}
              <div className="grid grid-cols-2 gap-4" data-testid="price-comparison">
                <div className="rounded-2xl border border-gray-100 p-4 text-center">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">{id.fornitore || "Attuale"}</p>
                  <p className="text-xl font-bold text-gray-800">€{Number(conf.prezzo_unitario_originale || 0).toFixed(4)}</p>
                  <p className="text-[10px] text-gray-400">/{isLuce ? "kWh" : "Smc"}</p>
                </div>
                <div className="rounded-2xl border border-green-200 bg-green-50/50 p-4 text-center">
                  <p className="text-[10px] text-green-600 uppercase tracking-wider mb-1">8energy</p>
                  <p className="text-xl font-bold text-green-700">€{Number(conf.prezzo_unitario_8energy || 0).toFixed(4)}</p>
                  <p className="text-[10px] text-gray-400">/{isLuce ? "kWh" : "Smc"} ({isLuce ? "PUN" : "PSV"} + spread)</p>
                </div>
              </div>

              {/* Consumption */}
              <div className="rounded-2xl border border-gray-100 p-4">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Consumi Estratti</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-gray-50 rounded-xl p-3 text-center"><p className="text-[10px] text-gray-400 uppercase">Totale</p><p className="text-sm font-bold text-gray-800">{cons.totale_periodo || 0} {cons.unita}</p></div>
                  {cons.f1 != null && <div className="bg-red-50 rounded-xl p-3 text-center"><p className="text-[10px] text-red-400 uppercase">F1 Punta</p><p className="text-sm font-bold text-red-700">{cons.f1} {cons.unita}</p></div>}
                  {cons.f2 != null && <div className="bg-amber-50 rounded-xl p-3 text-center"><p className="text-[10px] text-amber-500 uppercase">F2 Interm.</p><p className="text-sm font-bold text-amber-700">{cons.f2} {cons.unita}</p></div>}
                  {cons.f3 != null && <div className="bg-green-50 rounded-xl p-3 text-center"><p className="text-[10px] text-green-500 uppercase">F3 Fuori P.</p><p className="text-sm font-bold text-green-700">{cons.f3} {cons.unita}</p></div>}
                </div>
              </div>

              {/* Hidden Costs */}
              {hidden.length > 0 && (
                <div className="rounded-2xl border border-red-100 p-4" data-testid="hidden-costs">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-red-500 flex items-center gap-2"><ShieldAlert className="w-3.5 h-3.5" /> Costi Nascosti</h3>
                    <span className="text-xs font-bold bg-red-50 text-red-600 px-2 py-0.5 rounded-full">{hidden.length} voci — €{data.totale_costi_nascosti?.toFixed(2)}</span>
                  </div>
                  <div className="space-y-2">{hidden.map((c, i) => <HiddenCostCard key={i} cost={c} />)}</div>
                </div>
              )}

              {/* Notes */}
              {data.note_analisi && (
                <div className="rounded-2xl border border-gray-100 p-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-2"><Info className="w-3.5 h-3.5" /> Note Analisi</h3>
                  <p className="text-xs text-gray-600 leading-relaxed">{data.note_analisi}</p>
                </div>
              )}

              <div className="flex justify-center pt-2 pb-6">
                <Button variant="ghost" size="sm" onClick={() => { setFile(null); setData(null); setSelectedOfferId(""); }} className="text-xs text-gray-400" data-testid="reset-btn">
                  <X className="w-3 h-3 mr-1" /> Nuova Simulazione
                </Button>
              </div>
            </div>

            {/* RIGHT: PDF Viewer */}
            {showPdf && (
              <div className="order-1 xl:order-2 min-h-[600px]">
                <div className="sticky top-16 rounded-2xl border border-gray-100 overflow-hidden h-[calc(100vh-100px)]">
                  <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <FileWarning className="w-4 h-4 text-gray-400" />
                      <span className="text-xs font-semibold text-gray-500 uppercase">Bolletta Originale</span>
                    </div>
                    <span className="text-[10px] text-gray-400">{data.page_count} {data.page_count === 1 ? "pagina" : "pagine"}</span>
                  </div>
                  {pdfUrl ? (
                    <iframe src={pdfUrl} className="w-full" style={{ height: "calc(100% - 40px)" }} title="Bolletta" data-testid="pdf-viewer" />
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-400"><p className="text-sm">PDF non disponibile</p></div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Glossary Panel */}
          <AnimatePresence>
            {showGlossary && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden px-6 pb-6">
                <div className="rounded-2xl border border-gray-100 p-5 bg-gray-50/50" data-testid="glossary-panel">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-4 flex items-center gap-2"><Lightbulb className="w-3.5 h-3.5 text-amber-500" /> Glossario Tecnico</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {Object.entries(glossario).map(([key, entry]) => (
                      <div key={key} className="bg-white rounded-xl p-3 border border-gray-100">
                        <p className="text-xs font-bold text-green-800 mb-1">{entry.titolo}</p>
                        <p className="text-[11px] text-gray-500 leading-relaxed">{entry.descrizione}</p>
                        <p className="text-[9px] text-gray-400 mt-1 font-mono">{entry.dettaglio}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </div>
  );
}
